import React, { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { Shield } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { logoutUser } from "../api/users/user";

interface TypeRestrictedRouteProps {
  children: React.ReactNode;
  /** User types allowed to access this route, e.g. ["0"] or ["0", "2"] */
  allowedTypes: string[];
}

const LoadingSpinner = ({ message = "Loading..." }: { message?: string }) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
    <div className="flex flex-col items-center justify-center">
      <div className="relative">
        <div className="w-24 h-24 border-4 border-[#e761a0] border-t-[#e761a0] rounded-full animate-spin" />
        <Shield className="w-8 h-8 text-[#e761a0] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
      </div>
      <p className="text-[#e761a0] text-xl mt-6 font-medium">{message}</p>
    </div>
  </div>
);

export const TypeRestrictedRoute: React.FC<TypeRestrictedRouteProps> = ({
  children,
  allowedTypes,
}) => {
  const { userDetails } = useAuth();
  const userType = userDetails?.type;

  const hasAccess =
    userType != null && allowedTypes.includes(userType);

  // When access is denied, run logout in an effect (never during render) to avoid
  // race conditions, strict-mode double-invocation, and setState-on-unmounted errors.
  useEffect(() => {
    if (!hasAccess && userType != null) {
      let cancelled = false;
      logoutUser().catch(() => {
        if (!cancelled) {
          window.location.href = "/login";
        }
      });
      return () => {
        cancelled = true;
      };
    }
  }, [hasAccess, userType]);

  if (userDetails === undefined || userDetails === null) {
    return <LoadingSpinner />;
  }

  if (!userType) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAccess) {
    return <LoadingSpinner message="Logging out..." />;
  }

  return <>{children}</>;
};
